import React, { useEffect } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";

import { resizableRoute } from "pages/_components/Resizable";
import { push, routerActions } from "react-router-redux";

import { AgendaPage, Button, Text } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";

import { actions as agendaActions, selectors as agendaSelectors } from "reducers/frequentDestination";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { actions as transferActions } from "reducers/transfer";
import { actions as statusActions } from "reducers/status";
import { actions as layoutActions } from "reducers/layout";

import * as i18n from "util/i18n";
import getTransferPath from "util/transfers";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";

import { getAccountByCbu } from "middleware/transfer";

function Agenda({
    location,
    isDesktop,
    fetching,
    fetchingExport,
    dispatch,
    contactList,
    idEnvironment,
    activeEnvironment,
    idContactDetail,
    initialSameHolder,
}) {
    const uxImprovementsDasboard = isUxDashboardImprovementsEnabled();
    const homePath = getPostLoginHomePath(uxImprovementsDasboard);

    useEffect(() => {
        if (activeEnvironment.permissions.frequentDestination) {
            dispatch(agendaActions.loadListRequest(idEnvironment, ""));
            dispatch(statusActions.saveLastHref(location));
        } else {
            dispatch(push(homePath));
        }
    }, [dispatch, idEnvironment, activeEnvironment.permissions.frequentDestination, location, homePath]);

    const handleBack = () => dispatch(push(homePath));

    const handleClickDownload = async () => dispatch(agendaActions.downloadListFrequentDestinations());

    const actionsCallbacks = (id, key) => {
        switch (key) {
            case "transfer":
                if (
                    activeEnvironment.permissions.transferToContact ||
                    activeEnvironment.permissions.transferThirdParties
                ) {
                    const contact = contactList.find(({ idFrequentDestination }) => idFrequentDestination === id);
                    dispatch(
                        transferActions.loadThirdPartiesTransfer({
                            cbu: contact.cbu,
                            email: contact.email,
                            transferKind: contact.isOwn === "1" ? "same" : "other",
                            isFrequentDestinations: true,
                        }),
                    );
                    dispatch(
                        routerActions.push(
                            getTransferPath(
                                activeEnvironment.permissions.transferToContact ? "toContact" : "thirdParties",
                            ),
                        ),
                    );
                }
                break;
            case "modify":
                dispatch(agendaActions.modifyFrequentDestinationPreRequest(id, true));
                break;
            case "delete":
                dispatch(agendaActions.deleteFrequentDestinationPreviewRequest(id));
                break;
            case "share":
                dispatch(
                    notificationActions.showNotification(i18n.get("agenda.action.success.share"), "success", [
                        "agenda",
                    ]),
                );
                break;
            case "copy":
                dispatch(
                    notificationActions.showNotification(i18n.get("agenda.action.success.copy"), "success", ["agenda"]),
                );
                break;
            default:
                break;
        }
    };

    const actionsLabels = (key) => {
        if (
            key === "transfer" &&
            !activeEnvironment.permissions.transferAccess &&
            !activeEnvironment.permissions.transferToContact &&
            !activeEnvironment.permissions.transferThirdParties
        ) {
            return "";
        }
        if ((key === "modify" || key === "delete") && !activeEnvironment.permissions["frequentDestination.manage"]) {
            return "";
        }
        return i18n.get(`agenda.contextMenu.${key}`);
    };

    const searchAlias = async (cbucvu, id) => {
        dispatch(agendaActions.setIdContactDetail(id));
        const {
            data: { data },
        } = await getAccountByCbu(cbucvu);
        return data.accountDetail?.cbuAlias || "";
    };

    const rigthContent = (
        <Button
            icon="SendDown"
            iconVariant="outline"
            variant={isDesktop ? "outline" : "text"}
            color={isDesktop ? "primary" : "white"}
            onClick={handleClickDownload}
            isInactive={fetchingExport}
            loading={fetchingExport && isDesktop}
            aria-label={i18n.get("global.download")}
            text={isDesktop ? i18n.get("agenda.download") : undefined}
            w={isDesktop ? "200px" : undefined}
            className="icon-1-2"
        />
    );

    useEffect(() => {
        if (!uxImprovementsDasboard) {
            return undefined;
        }
        dispatch(layoutActions.setRightContentMobile(rigthContent));
        dispatch(layoutActions.setGoBackPath("/v3/dashboard"));
        dispatch(layoutActions.setTitleKey("agenda.title"));
        dispatch(layoutActions.setScopes(["agenda"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setRightContentMobile(null));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setTitleKey(""));
        };
    }, [dispatch, uxImprovementsDasboard]);

    return (
        <>
            {!uxImprovementsDasboard ? <Notification scopeToShow="agenda" /> : null}
            {!uxImprovementsDasboard ? <Notification scopeToShow="FrequentDestinations" /> : null}
            <div className="admin-detail-head px-5">
                {isDesktop && <Head onBack={handleBack} />}
                {(!uxImprovementsDasboard || isDesktop) && (
                    <Head
                        accessibilityTextId="agenda.title"
                        titleClassName={
                            !isDesktop ? "header-mobile-title-background-blue white-space-normal align-center" : ""
                        }
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile blue-main-title-mobile background-transparent" : ""
                        }
                        centerElement={() => (
                            <Text as="h1" className="m-0">
                                {i18n.get("agenda.title")}
                            </Text>
                        )}
                        onBack={!isDesktop && handleBack}
                        rightContent={() => rigthContent}
                    />
                )}
            </div>
            <AgendaPage
                contactsArray={contactList.map((contact) => ({
                    id: contact.idFrequentDestination,
                    cbucvu: contact.cbu,
                    type: contact.productType,
                    bank: `${contact.localBank}${!contact.cbu.startsWith("000") ? `/${contact.subsidiaryBank}` : ""}`,
                    name: contact.recipientName,
                    reference: contact.name,
                    number: contact.recipientDocumentNumber,
                    email: contact.recipientEmail,
                    isOwn: contact.isOwn,
                    document: `${contact.recipientDocumentType} ${contact.recipientDocumentNumber}`,
                }))}
                getActions={actionsCallbacks}
                getActionsLabel={actionsLabels}
                getLabel={(key) => i18n.get(`agenda.detail.label.${key}`)}
                loading={fetching}
                defaultContactDetailID={idContactDetail}
                onAddContact={() => {
                    dispatch(agendaActions.resetListFrequentDestinations());
                    dispatch(push("/v2/agenda/create"));
                }}
                getAlias={searchAlias}
                initialValueFilterbySameTitular={initialSameHolder}
                getFilterbySameTitular={(v) => dispatch(agendaActions.setSameHolder(v))}
                showAddContact={activeEnvironment.permissions["frequentDestination.manage"]}
            />
        </>
    );
}

Agenda.propTypes = {
    dispatch: func.isRequired,
    location: shape({
        pathname: string,
    }).isRequired,
    contactList: arrayOf(
        shape({
            name: string,
            recipientName: string,
            recipientDocumentType: string,
            recipientDocumentNumber: string,
            cbu: string,
            recipientEmail: string,
        }),
    ).isRequired,
    fetching: bool.isRequired,
    idEnvironment: number.isRequired,
    isDesktop: bool.isRequired,
    fetchingExport: bool.isRequired,
    activeEnvironment: shape({
        permissions: arrayOf(string),
    }).isRequired,
    idContactDetail: string.isRequired,
    initialSameHolder: string.isRequired,
};

const mapStateToProps = (state) => ({
    contactList: agendaSelectors.getListFrequentDestinations(state),
    idEnvironment: agendaSelectors.getEnviromentId(state),
    fetching: agendaSelectors.getFetching(state),
    fetchingExport: agendaSelectors.getFetchingExport(state),
    idContactDetail: agendaSelectors.getIdContactDetail(state),
    initialSameHolder: agendaSelectors.getSameHolder(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(Agenda);
